'use client';

import { useState, useRef, useEffect } from 'react';
import { Slider } from '@eliadmin/core/src/components/ui/slider';

interface Hotspot {
  id: string;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

interface HotspotImageProps {
  imageSrc: string;
  fallbackSrcs: string[];
  alt: string;
  hotspots: Hotspot[];
  onHotspotHover?: (hotspotId: string | null) => void;
  hoveredHotspotIds?: string[];
  className?: string;
  onRetryFetchComponentGroup?: () => void;
}

// Image loader logic from loader.js
function getImageUrl(src: string, width?: number, quality?: number): string {
  const isLocal = !src.startsWith('http');
  const query = new URLSearchParams();

  const imageOptimizationApi =
    process.env.IMAGE_OPTIMIZATION_API ?? 'https://eliadmin.b-cdn.net/image';

  const baseUrl = process.env.NEXT_PUBLIC_APP_URL;

  const fullSrc = `${baseUrl}${src}`;

  if (width) query.set('width', width.toString());
  if (quality) query.set('quality', quality.toString());

  if (isLocal && process.env.NODE_ENV === 'development') {
    return src;
  }

  if (isLocal) {
    return `${imageOptimizationApi}/${fullSrc}?${query.toString()}`;
  }

  // For external images
  return `${imageOptimizationApi}/${src}?${query.toString()}`;
}

// Test if an image URL is valid
function testImageUrl(url: string): Promise<boolean> {
  return new Promise(resolve => {
    if (!url) {
      resolve(false);
      return;
    }

    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = getImageUrl(url);
  });
}

export function HotspotImage({
  imageSrc,
  fallbackSrcs,
  alt,
  hotspots,
  onHotspotHover,
  hoveredHotspotIds,
  className,
  onRetryFetchComponentGroup,
}: HotspotImageProps) {
  // Remove the minZoom clamping, allow zoom from 0.1
  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageReady, setImageReady] = useState(false);
  const [workingImageUrl, setWorkingImageUrl] = useState<string>('');
  const [parentSize, setParentSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const [imageSize, setImageSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const imageRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const hasRetriedRef = useRef(false);

  // Reset pan position to center on image, parent size, or zoom change
  useEffect(() => {
    setPosition({ x: 0, y: 0 });
  }, [workingImageUrl, parentSize.width, parentSize.height, zoom]);

  // Track parent container size
  useEffect(() => {
    if (!parentRef.current) return;
    const updateSize = () => {
      const rect = parentRef.current!.getBoundingClientRect();
      setParentSize({ width: rect.width, height: rect.height });
    };
    updateSize();
    const resizeObserver = new window.ResizeObserver(updateSize);
    resizeObserver.observe(parentRef.current);
    return () => resizeObserver.disconnect();
  }, [parentRef]);

  // Track image natural size
  useEffect(() => {
    if (!workingImageUrl) return;
    const img = new window.Image();
    img.onload = () => {
      setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.src = getImageUrl(workingImageUrl);
  }, [workingImageUrl]);

  // Test images from high to low resolution
  useEffect(() => {
    const testImages = async () => {
      // Start with the main image source
      const allImages = [imageSrc, ...fallbackSrcs];

      for (const imageUrl of allImages) {
        if (await testImageUrl(imageUrl)) {
          setWorkingImageUrl(imageUrl);
          setImageReady(true);
          hasRetriedRef.current = false; // reset for next time
          return;
        }
      }

      // If no images work, try to refetch once
      if (onRetryFetchComponentGroup && !hasRetriedRef.current) {
        hasRetriedRef.current = true;
        onRetryFetchComponentGroup();
        return;
      }

      // If already retried, show placeholder
      setWorkingImageUrl('/images/spareparts/placeholder_ktm.png');
      setImageReady(true);
    };

    testImages();
  }, [imageSrc, fallbackSrcs]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      e.preventDefault();
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;

      // Add some constraints to prevent dragging too far
      const maxOffset = 200 * zoom;
      setPosition({
        x: Math.max(-maxOffset, Math.min(maxOffset, newX)),
        y: Math.max(-maxOffset, Math.min(maxOffset, newY)),
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch handlers for mobile devices
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      e.stopPropagation();
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y,
      });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isDragging && e.touches.length === 1) {
      e.stopPropagation();
      const newX = e.touches[0].clientX - dragStart.x;
      const newY = e.touches[0].clientY - dragStart.y;

      // Add some constraints to prevent dragging too far
      const maxOffset = 200 * zoom;
      setPosition({
        x: Math.max(-maxOffset, Math.min(maxOffset, newX)),
        y: Math.max(-maxOffset, Math.min(maxOffset, newY)),
      });
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    console.log('Touch end');
    e.stopPropagation();
    setIsDragging(false);
  };

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      setIsDragging(false);
    };

    const handleGlobalTouchEnd = () => {
      setIsDragging(false);
    };

    const handleGlobalTouchMove = (e: TouchEvent) => {
      if (isDragging) {
        e.preventDefault();
      }
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);
    document.addEventListener('touchend', handleGlobalTouchEnd, { passive: false });
    document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('touchend', handleGlobalTouchEnd);
      document.removeEventListener('touchmove', handleGlobalTouchMove);
    };
  }, [isDragging]);

  // Calculate scale and offset for object-fit: contain + zoom
  let fitScale = 1;
  let scale = zoom;
  let offsetX = 0;
  let offsetY = 0;
  if (parentSize.width && parentSize.height && imageSize.width && imageSize.height) {
    const scaleX = parentSize.width / imageSize.width;
    const scaleY = parentSize.height / imageSize.height;
    fitScale = Math.min(scaleX, scaleY);
    scale = fitScale * zoom;
    // Center the image
    offsetX = (parentSize.width - imageSize.width * scale) / 2;
    offsetY = (parentSize.height - imageSize.height * scale) / 2;
  }

  // Calculate display size: fit within 600px height and max 573px width, maintain aspect ratio
  let displayHeight = 600;
  let displayWidth =
    imageSize.width && imageSize.height ? (imageSize.width / imageSize.height) * 600 : 'auto';
  if (typeof displayWidth === 'number' && displayWidth > 573) {
    displayWidth = 573;
    displayHeight =
      imageSize.width && imageSize.height ? 573 / (imageSize.width / imageSize.height) : 600;
  }

  // Hotspot overlay component
  const HotspotOverlay = () => {
    if (!workingImageUrl || hotspots.length === 0) {
      return null;
    }

    return (
      <div className="pointer-events-auto absolute inset-0">
        {hotspots.map(hotspot => {
          const left = hotspot.startX * 100;
          const top = (1 - hotspot.startY - (hotspot.endY - hotspot.startY)) * 100; // Invert Y-axis and move one height higher
          const width = (hotspot.endX - hotspot.startX) * 100;
          const height = (hotspot.endY - hotspot.startY) * 100;

          // Check if this hotspot is currently hovered
          const isHovered = hoveredHotspotIds?.includes(hotspot.id);

          return (
            <div
              key={hotspot.id}
              className={`absolute border-[3px] transition-all duration-200 [border-radius:50%!important] ${
                isHovered ? 'border-primary bg-primary/10' : 'border-transparent bg-transparent'
              }`}
              style={{
                left: `${left}%`,
                top: `${top}%`,
                width: `${width}%`,
                height: `${height}%`,
              }}
              onMouseEnter={() => {
                onHotspotHover?.(hotspot.id);
              }}
              onMouseLeave={() => {
                // Only clear if we're not hovering the spare part list
                setTimeout(() => {
                  onHotspotHover?.(null);
                }, 100);
              }}
              onTouchStart={e => {
                e.stopPropagation();
                onHotspotHover?.(hotspot.id);
              }}
              onTouchEnd={e => {
                e.stopPropagation();
                // Keep the hotspot highlighted for a moment on touch
                setTimeout(() => {
                  onHotspotHover?.(null);
                }, 500);
              }}
            />
          );
        })}
      </div>
    );
  };

  return (
    <div className={className}>
      <div className="mb-4 flex flex-col items-center justify-between md:flex-row">
        <h4 className="text-lg font-semibold text-gray-900">{alt}</h4>

        {/* Zoom Slider */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Zoom:</span>
          <div className="w-32">
            <Slider
              value={[zoom]}
              onValueChange={value => setZoom(value[0])}
              min={0.1}
              max={2}
              step={0.1}
              className="w-full"
            />
          </div>
          <span className="w-8 text-right text-sm text-gray-600">{Math.round(zoom * 100)}%</span>
        </div>
      </div>

      {/* Image Container */}
      {imageReady && (
        <div
          ref={parentRef}
          className="relative flex h-[70vh] w-auto items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-gray-50 sm:h-[400px] md:h-[500px] lg:h-[600px]"
          style={{ touchAction: 'none' }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Touch overlay for mobile */}
          <div
            className="absolute inset-0 z-10 cursor-grab active:cursor-grabbing lg:hidden"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            style={{ touchAction: 'none' }}
          />
          <div
            ref={imageRef}
            className="cursor-grab touch-none select-none active:cursor-grabbing"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={() => setIsDragging(false)}
            style={{
              transform: `translate(${offsetX + position.x}px, ${offsetY + position.y}px) scale(${scale})`,
              transformOrigin: 'top left',
              transition: isDragging ? 'none' : 'transform 0.1s ease-out',
              height: displayHeight,
              width: displayWidth,
              touchAction: 'none',
            }}
          >
            <div
              className="pointer-events-none h-full  bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url(${getImageUrl(workingImageUrl)})`,
                width: displayWidth,
                height: displayHeight,
              }}
              role="img"
              aria-label={alt}
            />
            <HotspotOverlay />
          </div>
        </div>
      )}
    </div>
  );
}
